import { MergeCellsOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Col, Form, message, Modal, Row, Space, Spin, Typography } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useLoginModal } from '../../../../context/loginModalContext'
import { useUser } from '../../../../context/userContext'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import SearchField from '../../../common/searchFields/SearchField'
import { MERGE_PATIENT } from '@dentolize/clinic-mobile/src/shared/store/mutations/companyMutations'
import { SEARCH_PATIENT_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'

const MergePatientButton = ({ patient, handleAnimateRow, handleClose }) => {
  const [visible, setVisible] = useState(false)

  const { id } = useParams()
  const { user } = useUser()
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setLoginModalVisible } = useLoginModal()

  const [mergeItem, { loading }] = useMutation(MERGE_PATIENT, {
    update: handleUpdateAfterDelete
  })

  function handleUpdateAfterDelete(store) {
    store.data.delete(`Patient:${patient.id}`)
    handleAnimateRow && handleAnimateRow(null, true)
  }

  const { validateFields, scrollToField, resetFields } = form

  useEffect(() => {
    resetFields()
  }, [visible])

  const toggleVisible = () => setVisible(visible => !visible)

  const handleSubmit = () =>
    validateFields()
      .then(async values => {
        try {
          if (patient.id === values.newPatient.key) {
            message.error(t('app.mergeError'))
            return
          }
          await mergeItem({
            variables: {
              patient: patient.id,
              newPatient: values.newPatient.key
            }
          })
          id && navigate('/')
          handleClose && handleClose()
          message.success(t('common.updatedSuccessfully'))
          toggleVisible()
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        scrollToField(errorFields?.[0]?.name)
      })

  return (
    <>
      <Modal
        open={visible}
        title={t('app.mergePatient')}
        okButtonProps={{ loading, htmlType: 'submit', form: 'MergePatientForm', 'data-testid': 'patientForm-merge-confirm' }}
        onCancel={toggleVisible}
      >
        <Spin spinning={loading}>
          <Space direction="vertical" size={0}>
            <Typography.Text type="danger" strong>
              {t('app.mergePatientAlert', {
                val: `${patient.firstName} ${patient.lastName}`
              })}
            </Typography.Text>
            <Typography.Text type="secondary">{t('app.mergePatientSubtitle')}</Typography.Text>
          </Space>
          <br />
          <br />
          <Form onFinish={handleSubmit} form={form} name="MergePatientForm" layout="vertical" scrollToFirstError>
            <Row type="flex" gutter={16}>
              <Col span={24}>
                <SearchField
                  GET_QUERY={SEARCH_PATIENT_QUERY}
                  label={t('patients.newPatient')}
                  fieldName="newPatient"
                  dataName="searchPatients"
                  rules={[{ required: true, message: t('app.fieldRequired') }]}
                />
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
      <Button
        className="warningBtn"
        onClick={toggleVisible}
        icon={<MergeCellsOutlined />}
        disabled={!user.permissions.disablePatient}
        data-testid="patientForm-merge"
      >
        {t('app.mergePatient')}
      </Button>
    </>
  )
}

MergePatientButton.propTypes = {
  patient: PropTypes.object.isRequired,
  handleAnimateRow: PropTypes.func
}

export default MergePatientButton
